<template>
    <div class="h-100 content__page">
        <div class="mt--20" :class="{ 'min-h-inherit': !groupByKeys.length }">
            <div v-if="showActionBtns" class="content__page__header">
                <div class="list--count">
                    <FormLabel title="Total meetings" label-type="title">
                        <span class="ml--3 zm__count__badge text--xs">{{ meetings.length }}</span>
                    </FormLabel>
                </div>
                <div class="flex al=-8999-9 -center gap-2">
                    <DateRangeFilter v-model="filter" @update:filter="setFilter" />
                    <InnerPageFilter
                        v-model="filter" 
                        page-type="client_meetings" 
                        @update-filter="params => handleFetchMeetings(params, true)"
                        @clear="params => handleFetchMeetings(params, false)"
                    />
                </div>
            </div>

            <div class="zm__content">
                <div class="zm__content__left" :class="{ 'empty': !groupByKeys.length }">
                    <template v-if="pageState.loading">
                        <PageSpinner />
                    </template>

                    <EmptyPage v-else-if="pageState.err" state="error">
                        <template #button>
                            <BaseButton
                                label="Reload" 
                                icon-name="refresh"
                                @submit="handleFetchMeetings"
                            />
                        </template> 
                    </EmptyPage>

                    <template v-else>
                        <template v-if="!groupByKeys.length">
                            <EmptyPage 
                                v-if="!isSearched"
                                state="empty_custom" 
                                icon-name="calendar"
                                btn-state="none"
                                position="left"
                                title="No existing meetings" 
                                subtitle="Meetings are a great way to keep track of virtual or physcial appointments with clients. To get started, ensure you have at least one connected calendar via integrations."
                            />

                            <EmptyPage v-else state="no_result" />
                        </template>
                        
                        <template v-else>
                            <MeetingList 
                                :items="meetings"
                                :current-meeting-id="currentMeetingItem?._id"
                                @fetch-single-meeting="handleFetchSingleMeeting"
                            />
                        </template>
                    </template>
                </div>

                <div v-if="groupByKeys.length" class="zm__content__right">
                    <template v-if="currentMeetingItem._id">
                        <SingleMeeting 
                            :current-meeting-item="currentMeetingItem" 
                            :page-loading="currentItemState.loading"
                            :item-loading="deleteLoading"
                            :err="currentItemState.err"
                            :note-action="noteAction"
                            :current-note="currentNote"
                            @open-note="openNoteModal"
                            @open-delete="openDeleteMeetingModal"
                            @delete-note="onDeleteCompleteNote" 
                            @open-edit="openEditMeetingModal"
                            @duplicate-complete="onDuplicateComplete"
                        />
                    </template>

                    <EmptyPage 
                        v-else
                        state="empty_custom" 
                        icon-name="calendar"
                        title="No meeting selected" 
                        subtitle="Select a meeting to view meeting details"
                    />
                </div>
            </div>
        </div>

        <MeetingNoteModal 
            :meeting-id="currentMeetingItem?._id" 
            :action="noteAction"
            :show-modal="showNoteModal" 
            :current-note="currentNote"
            @complete="handleNoteComplete" 
            @cancel="closeNoteModal" 
        />

        <ConfirmDeletion
            type="meeting"
            :show-modal="modalAction === 'showDeleteModal'"
            :loading="deleteLoading"
            @cancel="closeDeleteMeetingModal" 
            @delete="deleteMeeting"
        />

        <UpgradeAccountModal 
            :show-modal="showUpgradeModal|| showUpgradeModalAlt"  
            type="meetings" 
            @cancel="closeUpgradeModal"
        >
            <template #text>
                <div>
                    <template v-if="accountPlan === 'free'">
                        <p class="text--sm text-gray-400">
                            This is a paid feature available on the <strong>Standard</strong> and <strong>Plus</strong> plans.
                        </p>
                        <p class="text--sm text-gray-400 mt--15">
                            To unlock this feature, upgrade your account in your account settings.
                        </p>
                    </template>

                    <template v-if="accountPlan === 'standard'">
                        <p class="text--sm text-gray-400">
                            You have exhausted the number of meeting downloads allowed on the <strong>Standard</strong> account plan.
                        </p>
                        <p class="text--sm text-gray-400 mt--15">
                            To download more meetings, go to your account settings and upgrade your account to a <strong>Plus</strong> plan.
                        </p>
                    </template>
                </div>
            </template>
        </UpgradeAccountModal>

        <CreateMeetingModal 
            :show-modal="modalAction === 'showMeetingModal'" 
            :action="meetingModalType"
            modal-type="clients"
            :selected-meeting="currentMeetingItem"
            :client-list-prop="[currentClient]"
            :calendars="calendarIntegrations"
            @close="closeMeetingModal"
            @create-complete="onCompleteCreateMeeting" 
            @edit-complete="onCompleteEditMeeting" 
        />
    </div>
</template>

<script>
import MeetingList from "@/components/meetings/MeetingList.vue";
import SingleMeeting from "@/components/meetings/SingleMeeting.vue";
import DateRangeFilter from "@/components/ui/filter/DateRange.vue";
import { 
    IconSvg, 
    Pagination, 
    PageSpinner, 
    EmptyPage,
    BaseButton, 
    InnerPageFilter,
    FormLabel,
    MeetingNoteModal,
    ConfirmDeletion,
    UpgradeAccountModal,
    CreateMeetingModal,
} from "@/components/ui/index";
import { MAX_COUNT_BY_COLLECTIONS } from "@/utils/enums";
import { groupList, assembleQueryList } from "@/utils/helpers";
import api from "@/api/url";

export default {
    name: "ClientMeetingsView",

    components: {
        IconSvg,
        EmptyPage,
        Pagination, 
        MeetingList,
        MeetingNoteModal,
        PageSpinner,
        BaseButton, 
        InnerPageFilter, 
        MeetingNoteModal,
        FormLabel,
        SingleMeeting,
        ConfirmDeletion,
        UpgradeAccountModal,
        CreateMeetingModal,
        DateRangeFilter,
    },

    props: {
        clientEmail: {
            type: String,
            default: "",
        },

        currentTab: {
            type: [Object, undefined],
            default: undefined,
        },

        refreshMeetingList: {
            type: Boolean,
            default: false,
        },

        selectedMeeting: {
            type: [Object, undefined],
            default: undefined,
        },

        currentClient: {
            type: Object,
            default: () => {},
        },

        calendarIntegrations: {
            type: Array,
            default: () => [],
        },
    },

    emits: ["reset-refresh"],

    data() {
        return {
            pageState: {
                loading: false,
                err: false,
                errMsg: "",
            },
            currentMeetingItem: {
                _id: "",
                title: "",
                description: "",
                startTime: null,
                endTime: null,
                attendees: [],
            },
            currentItemState: {
                loading: false,
                err: false,
                errMsg: "",
            },
            showNoteModal: false,
            noteAction: "add",
            currentNote: undefined,
            deleteLoading: false,
            meetings: [],
            pageData: {
                currentPage: 1,
                totalPages: 0,
                pageSize: 0,
                total: 0,
            },
            filter: {},
            showUpgradeModalAlt: false,
            isSearched: false,
            meetingModalType: "",
            showMeetingModal: false,
            modalAction: "",
        };
    },

    computed: {
        showPagination() {
            if (this.pageData?.totalPages > 1) {
                return true;
            } else {
                return false;
            }
        },

        userFullName() {
            return this.profile?.fullName || "";
        },

        userEmail() {
            return this.profile?.email || "";
        },

        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },

        groupedListByDate() {
            if (!this.meetings.length) {
                return [];
            }

            const arr = groupList(this.meetings, (item) => {
                // createdAt comes in form of timestamp from API
                // Convert to YYYY-MM-DD
                const date = new Date(item.createdAt);

                const year = date.getFullYear();
                const month = date.getMonth() + 1;
                const day = date.getDate();

                const filter = `${year}-${month}-${day}`;

                return filter;
            });

            return Object.fromEntries(arr);
        },

        groupByKeys() {
            return Object.keys(this.groupedListByDate) || [];
        },

        showUpgradeModal() {
            return this.$store.getters["app/showUpgradeModal"];
        },

        checkUpgradeLoading() {
            return this.$store.getters["app/checkUpgradeLoading"];
        },

        checkUpgradeFields() {
            return this.$store.getters["app/checkUpgradeFields"];
        },

        showActionBtns() {
            if (this.pageData?.total) {
                return true;
            }
            if (this.isSearched) {
                return true;
            }

            return false;
        },

        clients() {
            return this.$store.getters["clients/clients"].filter(c => {                
                if (c.email !== this.clientEmail) {                    
                    return c;
                }
            });
        },
    },

    watch: {
        currentTab: {    
            async handler(newVal) {
                if (newVal?.name === "Meetings" && !this.meetings.length) {
                    await this.handleFetchMeetings({ refresh: true });
                }
            },
            deep: true,
            immediate: true,                    
        },

        refreshMeetingList: {
            handler(newVal) {
                if (newVal && this.selectedMeeting?.title) {
                    this.meetings = [ ...this.selectedMeeting, ...this.meetings ];
                }
            },                    
        },
    },
    
    methods: {
        closeUpgradeModal() {
            if (this.showUpgradeModalAlt) {
                this.showUpgradeModalAlt = false;
                return;
            }
            return this.$store.dispatch("app/closeUpgradeModal", "meetings");
        },

        async handleFetchMeetings(params = {}, search = false, reload = true) {
            try {                
                if (reload) {
                    this.pageState.loading = true;
                }
                
                if (search) {
                    this.isSearched = true;
                } else {
                    this.isSearched = false;
                }

                this.pageState.err = false;
                this.pageState.errMsg = "";

                const pageQueryObj = {
                    page: params?.pageNumber || this.pageData?.currentPage || 1,
                    email: this.clientEmail,
                    ...params,
                };

                if (params?.endDate) {
                    let endDate;
                    const currentDate = params?.startDate;

                    switch (params.endDate) {
                    case "a_week_after": // 1 week after the current date
                        endDate = new Date(currentDate);
                        endDate.setDate(currentDate.getDate() + 7);
                        break;

                    case "two_weeks_after": // 2 weeks after the current date
                        endDate = new Date(currentDate);
                        endDate.setDate(currentDate.getDate() + 14);
                        break;

                    case "current_month": // The end of the current month
                        endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
                        break;

                    default:
                        endDate = new Date(currentDate);
                        endDate.setDate(currentDate.getDate() + 7);
                        break;
                    }
                        
                    pageQueryObj.endDate = endDate;
                }

                const buildResult = assembleQueryList(pageQueryObj);
                const response = await api.meeting.getMeetings(buildResult);

                console.log({ handleFetchMeetings: response });

                this.meetings = response?.data?.data?.data;
                this.pageData = response?.data.data?.pageDetails;

                const firstItemInArrId = this.meetings[0]?._id;
                await this.handleFetchSingleMeeting(firstItemInArrId);
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;

                this.pageState.err = true;
                this.pageState.errMsg = errMsg;
            } finally {
                if (reload) {
                    this.pageState.loading = false;
                }
            }
        },

        async handleFetchSingleMeeting(id) {
            try {
                if (!id) {
                    throw new Error("Missing meeting id required");
                }

                this.currentItemState.loading = true;
                this.currentItemState.err = false;
                this.currentItemState.errMsg = "";

                const meetingItemRes = await this.$store.dispatch("meeting/fetchOneMeeting", id);

                this.currentMeetingItem = {
                    ...this.currentMeetingItem,
                    ...meetingItemRes,
                };
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.currentItemState.err = true;
                this.currentItemState.errMsg = errMsg;
            } finally {
                this.currentItemState.loading = false;
            }
        },

        openNoteModal({ action, item }) {            
            if (action === "edit" && !item?._id) return;

            this.noteAction = action;

            if (item) {
                this.currentNote = item;
            }
            this.showNoteModal = true;
        },

        closeNoteModal() {
            this.noteAction = "";
            this.currentNote = undefined;
            this.showNoteModal = false;
        },

        handleNoteComplete(item) {            
            if (this.noteAction === "add") {
                this.currentMeetingItem.notes = [ item, ...this.currentMeetingItem.notes];
            } else {
                const updatedNotes = this.currentMeetingItem.notes.map(x => {
                    console.log({ x, item });
                    
                    if (x._id === item._id) {
                        return item;
                    } else {
                        return x;
                    }
                });

                console.log({ updatedNotes });
                
                this.currentMeetingItem.notes = updatedNotes;
            };
            this.currentNote = undefined;
        },

        async onDeleteCompleteNote(item) {
            try {
                await this.$store.dispatch("schedule/modifyMeetingNote", { id: this.currentMeetingItem?._id, note: item });

                this.currentMeetingItem.notes = this.currentMeetingItem.notes.filter(x => x !== item);
            } catch (error) {
                const errMsg = error?.response?.data?.message || error.message;
                this.$toast.error(errMsg);
            }
        },

        async openCreateMeeting() {
            try {
                if (["standard", "free"].includes(this.accountPlan)) {        
                    const maxLimit = MAX_COUNT_BY_COLLECTIONS[this.accountPlan].meetings.create;
    
                    if (this.meetings.length >= maxLimit) {
                        this.showUpgradeModalAlt = true;
                        return;
                    } 

                    if (this.isSearched) {
                        await this.$store.dispatch("app/checkUpgradeStatus", "meetings");
                    }
                }
    
                this.meetingModalType = "add"; 
                this.modalAction = "showMeetingModal";
            } catch (error) {
                const errMsg = error?.response?.data?.message || error.message;
                this.$toast.error(errMsg);
            }
        },

        closeMeetingModal() {
            this.modalAction = "";
            this.meetingModalType = "";
        },

        async onCompleteCreateMeeting() {  
            await this.handleFetchMeetings({}, false, false);          
            this.closeMeetingModal();
        },

        async openEditMeetingModal() {
            this.meetingModalType = "edit"; 
            this.modalAction = "showMeetingModal";
        },

        onCompleteEditMeeting(data) {            
            this.meetings = [ data, ...this.meetings ];
            this.closeMeetingModal();
        },

        openDeleteMeetingModal(item) {            
            this.currentMeetingItem = item;
            this.modalAction = "showDeleteModal";
        },

        closeDeleteMeetingModal() {
            this.currentMeetingItem = {
                _id: "",
                title: "",
                description: "",
                startTime: null,
                endTime: null,
                attendees: [],
            };
            this.modalAction = "";
        },

        async deleteMeeting() {
            if (!this.currentMeetingItem._id) return;

            try {
                this.deleteLoading = true;
                
                await this.$store.dispatch("meeting/removeMeeting", this.currentMeetingItem._id);

                this.$toast.success("Meeting deleted");
                
                const meetings = this.meetings.filter(x => x._id !== this.currentMeetingItem._id);

                this.meetings = meetings;
                this.pageData = {
                    ...this.pageData,
                    total: this.pageData.total - 1,
                    totalPages: Math.ceil(this.pageData.total - 1 / 50),
                };
                this.closeDeleteMeetingModal();
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.deleteLoading = false;
            }
        },

        async onDuplicateComplete() {            
            await this.handleFetchMeetings({}, false, false);    
        },

        setFilter(val) {
            this.filter = {
                ...this.filter,
                ...val,
            };
        },
    },
};
</script>

<style scoped lang="scss">
    .zm__content__left {
        margin-right: 0.875rem !important;
    }
</style>